import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Modal,
  Platform,
  Pressable,
  StyleSheet,
  Switch,
  View,
  useWindowDimensions,
} from 'react-native';
import PropTypes from 'prop-types';
import { useTheme } from '@/context/ThemeContext';
import Text from '@/components/Typography/Text';
import Icon from '@/components/Icon';
import Tooltip from '@/components/Tooltip';
import { useChatStore, useCoworkerStore, useToastStore } from '@/store';
import useTranslation from '@/hooks/useTranslation';
import useResponsiveness from '@/hooks/useResponsiveness';
import { isWorkActive } from '@/constants/work';
import {
  WORK_THINKING_LEVELS,
  WORK_THINKING_TRACK_COLOR,
  clampThinkingIndex,
  thinkingPowerById,
} from '@/constants/workThinkingPower';

const TRACK_HEIGHT = 10;
const THUMB_SIZE = 18;
const DOT_SIZE = 4;

function clientXFromEvent(event) {
  if (typeof event?.clientX === 'number') return event.clientX;
  const native = event?.nativeEvent;
  if (typeof native?.clientX === 'number') return native.clientX;
  if (typeof native?.pageX === 'number') return native.pageX;
  return null;
}

function rectFromEvent(event) {
  const nodes = [
    event?.currentTarget,
    event?.target,
    event?.nativeEvent?.currentTarget,
    event?.nativeEvent?.target,
  ];
  for (const node of nodes) {
    if (node && typeof node.getBoundingClientRect === 'function') {
      return node.getBoundingClientRect();
    }
  }
  return null;
}

function WorkThinkingPowerPanel({ level, onChange, colors, iconName = 'bolt' }) {
  const { t } = useTranslation();
  const max = WORK_THINKING_LEVELS.length - 1;
  const draggingRef = useRef(false);
  const levelIdRef = useRef(level.id);
  const previewIndexRef = useRef(null);
  const [previewIndex, setPreviewIndex] = useState(null);
  levelIdRef.current = level.id;

  const shown = previewIndex == null
    ? level
    : thinkingPowerById(WORK_THINKING_LEVELS[clampThinkingIndex(previewIndex)]?.id);
  const fill = max === 0 ? 1 : shown.index / max;

  useEffect(() => {
    previewIndexRef.current = null;
    setPreviewIndex(null);
  }, [level.id]);

  const previewAt = useCallback((index) => {
    const next = clampThinkingIndex(index);
    previewIndexRef.current = next;
    setPreviewIndex(next);
  }, []);

  const commitIndex = useCallback((index) => {
    const next = thinkingPowerById(WORK_THINKING_LEVELS[clampThinkingIndex(index)]?.id);
    if (next && next.id !== levelIdRef.current) {
      onChange(next);
      return;
    }
    previewIndexRef.current = null;
    setPreviewIndex(null);
  }, [onChange]);

  const pickFromEvent = useCallback((event) => {
    const rect = rectFromEvent(event);
    const clientX = clientXFromEvent(event);
    if (rect?.width > 0 && Number.isFinite(clientX)) {
      previewAt(((clientX - rect.left) / rect.width) * max);
      return;
    }
    const locationX = event?.nativeEvent?.locationX;
    const width = event?.currentTarget?.offsetWidth
      || event?.nativeEvent?.layout?.width;
    if (Number.isFinite(locationX) && width > 0) {
      previewAt((locationX / width) * max);
    }
  }, [max, previewAt]);

  const finishDrag = useCallback((event) => {
    const wasDragging = draggingRef.current;
    draggingRef.current = false;
    // Only release a capture we still hold: the mouse fallback events carry no
    // pointerId, and the browser fires a synthetic mouseleave after pointerup
    // has already released it.
    const node = event?.currentTarget;
    const pointerId = event?.pointerId;
    if (Number.isFinite(pointerId) && node?.hasPointerCapture?.(pointerId)) {
      node.releasePointerCapture(pointerId);
    }
    if (!wasDragging) return;
    if (previewIndexRef.current != null) commitIndex(previewIndexRef.current);
  }, [commitIndex]);

  const webPointer = Platform.OS === 'web' ? {
    tabIndex: 0,
    onPointerDown: (event) => {
      event.preventDefault();
      event.stopPropagation();
      draggingRef.current = true;
      event.currentTarget?.setPointerCapture?.(event.pointerId);
      pickFromEvent(event);
    },
    onPointerMove: (event) => {
      if (!draggingRef.current) return;
      pickFromEvent(event);
    },
    onPointerUp: finishDrag,
    onPointerCancel: finishDrag,
    onMouseDown: (event) => {
      event.preventDefault();
      event.stopPropagation();
      draggingRef.current = true;
      pickFromEvent(event);
    },
    onMouseMove: (event) => {
      if (!draggingRef.current) return;
      pickFromEvent(event);
    },
    onMouseUp: finishDrag,
    onMouseLeave: (event) => {
      if (event.buttons) return;
      finishDrag(event);
    },
    onKeyDown: (event) => {
      if (event.key === 'ArrowRight' || event.key === 'ArrowUp') {
        event.preventDefault();
        commitIndex(shown.index + 1);
      } else if (event.key === 'ArrowLeft' || event.key === 'ArrowDown') {
        event.preventDefault();
        commitIndex(shown.index - 1);
      }
    },
  } : {};

  return (
    <View style={styles.panel} accessibilityRole="adjustable">
      <View style={styles.headerRow}>
        <Icon name={iconName} size={13} color={colors.text_color_secondary} solid />
        <Text size="s" weight="semibold" numberOfLines={1} style={styles.headerName}>
          {shown.label}
        </Text>
      </View>

      <View
        accessibilityRole="slider"
        accessibilityLabel={t('chatInput.thinkingPower.title')}
        accessibilityValue={{
          min: 0,
          max,
          now: shown.index,
          text: shown.label,
        }}
        onStartShouldSetResponder={() => true}
        onMoveShouldSetResponder={() => true}
        onResponderGrant={pickFromEvent}
        onResponderMove={pickFromEvent}
        onResponderRelease={finishDrag}
        {...webPointer}
        style={styles.sliderHit}
      >
        <View
          pointerEvents="none"
          style={[styles.track, { backgroundColor: colors.solid_color_tertiary }]}
        >
          <View
            style={[
              styles.trackFill,
              {
                backgroundColor: WORK_THINKING_TRACK_COLOR,
                width: `${fill * 100}%`,
              },
            ]}
          />
          {WORK_THINKING_LEVELS.map((item) => (
            <View
              key={item.id}
              style={[
                styles.dot,
                {
                  left: `${(item.index / max) * 100}%`,
                  backgroundColor: item.index <= shown.index
                    ? 'rgba(255,255,255,0.85)'
                    : 'rgba(255,255,255,0.35)',
                },
              ]}
            />
          ))}
          <View
            style={[
              styles.thumb,
              {
                left: `${fill * 100}%`,
                backgroundColor: '#fff',
                borderColor: colors.border_color_secondary,
              },
            ]}
          />
        </View>
        <View style={styles.stepRow} pointerEvents="box-none">
          {WORK_THINKING_LEVELS.map((item) => (
            <Pressable
              key={item.id}
              accessibilityRole="button"
              accessibilityLabel={item.label}
              onPress={() => commitIndex(item.index)}
              style={styles.stepHit}
            />
          ))}
        </View>
      </View>

      <View style={styles.metaRow}>
        <Text size="xxs" color="tertiary">{t('chatInput.thinkingPower.axisLeft')}</Text>
        <Text size="xxs" color="tertiary" style={styles.metaRight}>
          {t('chatInput.thinkingPower.axisRight')}
        </Text>
      </View>
      <Text size="xs" color="secondary" style={styles.caption}>
        {t('chatInput.thinkingPower.caption')}
      </Text>
    </View>
  );
}

WorkThinkingPowerPanel.propTypes = {
  level: PropTypes.object.isRequired,
  onChange: PropTypes.func.isRequired,
  colors: PropTypes.object.isRequired,
  iconName: PropTypes.string,
};

function WorkThinkingPowerTrigger({
  level,
  autoApprove = false,
  compact = false,
  isDisabled = false,
  hideTooltip = false,
  onPress,
}) {
  const { t } = useTranslation();
  const { colors } = useTheme();
  const [hovered, setHovered] = useState(false);
  const label = t('chatInput.thinkingPower.triggerA11y', { level: level.label }) + (autoApprove ? ', Auto-approve credits on' : '');
  // Compact (mobile / narrow) is icon-only — brain reads as intelligence.
  // Desktop keeps bolt next to the Fast/Standard/High label.
  const iconName = compact ? 'brain' : 'bolt';

  return (
    <Tooltip label={t('chatInput.thinkingPower.tooltip')} disabled={isDisabled || hideTooltip}>
      <Pressable
        onPress={onPress}
        disabled={isDisabled}
        onHoverIn={() => setHovered(true)}
        onHoverOut={() => setHovered(false)}
        accessibilityRole="button"
        accessibilityLabel={label}
        accessibilityHint={t('chatInput.thinkingPower.tooltip')}
        style={[
          styles.trigger,
          compact && styles.triggerCompact,
          hovered && !isDisabled && { backgroundColor: colors.solid_color_tertiary },
          isDisabled && { opacity: 0.5 },
        ]}
      >
        <Icon
          name={iconName}
          size={compact ? 13 : 12}
          color={colors.text_color_secondary}
          solid
        />
        {!compact && (
          <Text size="s" numberOfLines={1} style={styles.triggerLabel}>
            {level.label}
          </Text>
        )}
        {autoApprove && <Text size="xxs" color="secondary">Auto</Text>}
        <Icon name="chevron-down" size={8} color={colors.text_color_tertiary} />
      </Pressable>
    </Tooltip>
  );
}

WorkThinkingPowerTrigger.propTypes = {
  level: PropTypes.object.isRequired,
  autoApprove: PropTypes.bool,
  compact: PropTypes.bool,
  isDisabled: PropTypes.bool,
  hideTooltip: PropTypes.bool,
  onPress: PropTypes.func,
};

function WorkThinkingPowerButton({
  conversationId = null,
  compact = false,
  isDisabled = false,
}) {
  const { t } = useTranslation();
  const { colors } = useTheme();
  const { isXS } = useResponsiveness();
  const { width: viewportWidth, height: viewportHeight } = useWindowDimensions();
  const [open, setOpen] = useState(false);
  const [anchor, setAnchor] = useState(null);
  const [menuHeight, setMenuHeight] = useState(300);
  const triggerRef = useRef(null);
  const { addToast } = useToastStore((state) => state.actions);
  const composerThinkingPower = useCoworkerStore((s) => s.composerThinkingPower);
  const setComposerThinkingPower = useCoworkerStore((s) => s.setComposerThinkingPower);
  const setTaskModel = useCoworkerStore((s) => s.setTaskModel);
  const composerAutoApprove = useCoworkerStore((s) => s.composerAutoApprove);
  const setComposerAutoApprove = useCoworkerStore((s) => s.setComposerAutoApprove);
  const setTaskAutoApprove = useCoworkerStore((s) => s.setTaskAutoApprove);
  const [savingAutoApprove, setSavingAutoApprove] = useState(false);
  const taskId = useChatStore((state) => {
    if (!conversationId || conversationId === 'new') return null;
    const chat = state.chats.find((c) => c.id === conversationId);
    const raw = chat?.coworkerSession;
    if (!raw) return null;
    if (typeof raw === 'object') return String(raw._id || raw.id || '') || null;
    return String(raw);
  });
  const taskStatus = useCoworkerStore((s) => (taskId ? s.tasks[taskId]?.status : null));
  const openAbove = Boolean(conversationId && conversationId !== 'new');
  const taskThinkingPower = useCoworkerStore((s) => (taskId ? s.tasks[taskId]?.thinkingPower : null));
  const taskAutoApprove = useCoworkerStore((s) => (taskId ? s.tasks[taskId]?.autoApprove : null));
  const autoApprove = taskId ? taskAutoApprove === true : composerAutoApprove;
  const changeAutoApprove = useCallback(async (enabled) => {
    if (!taskId) { setComposerAutoApprove(enabled); return; }
    setSavingAutoApprove(true);
    try { await setTaskAutoApprove(taskId, enabled); }
    catch (err) { addToast(err?.message || 'Failed to change Auto-approve', 'error'); }
    finally { setSavingAutoApprove(false); }
  }, [taskId, setComposerAutoApprove, setTaskAutoApprove, addToast]);

  const level = useMemo(() => (
    thinkingPowerById(taskThinkingPower || composerThinkingPower)
  ), [composerThinkingPower, taskThinkingPower]);

  const commitLevel = useCallback(async (next) => {
    if (!next || next.id === level.id) return;
    setComposerThinkingPower(next.id);
    if (!taskId) return;
    try {
      const out = await setTaskModel(taskId, next.id);
      if (out?.applied === 'next_turn' && isWorkActive(taskStatus)) {
        addToast(t('chatInput.thinkingPower.switchedNextTurn', { level: next.label }), 'default');
      }
    } catch (err) {
      addToast(err?.message || t('chatInput.thinkingPower.switchFailed'), 'error');
    }
  }, [addToast, level.id, setComposerThinkingPower, setTaskModel, t, taskId, taskStatus]);

  const openPopover = useCallback((event) => {
    const fromEvent = event?.currentTarget;
    const fromRef = triggerRef.current;
    const place = (rect) => {
      setAnchor(rect || null);
      setOpen(true);
    };
    const node = (fromEvent && typeof fromEvent.getBoundingClientRect === 'function')
      ? fromEvent
      : fromRef;
    if (node && typeof node.getBoundingClientRect === 'function') {
      place(node.getBoundingClientRect());
      return;
    }
    if (fromRef && typeof fromRef.measureInWindow === 'function') {
      fromRef.measureInWindow((x, y, width, height) => {
        place({
          top: y,
          left: x,
          width,
          height,
          bottom: y + height,
          right: x + width,
        });
      });
      return;
    }
    place(null);
  }, []);

  const showCompact = compact || isXS;
  const sheet = isXS || !anchor;
  const fitsBelow = anchor && viewportHeight - anchor.bottom >= menuHeight + 8;
  const fitsAbove = anchor && anchor.top >= menuHeight + 8;
  const placeAbove = fitsAbove && (openAbove || !fitsBelow);
  const menuTop = anchor ? Math.max(8, Math.min(
    placeAbove ? anchor.top - menuHeight - 6 : anchor.bottom + 6,
    viewportHeight - menuHeight - 8,
  )) : 8;
  const cardStyle = sheet
    ? styles.modalCard
    : [
      styles.popoverCard,
      { top: menuTop },
      { right: Math.max(8, viewportWidth - anchor.right) },
    ];

  return (
    <View ref={triggerRef} {...(Platform.OS === 'web' ? {} : { collapsable: false })}>
      <WorkThinkingPowerTrigger
        level={level}
        autoApprove={autoApprove}
        compact={showCompact}
        isDisabled={isDisabled}
        hideTooltip={open}
        onPress={openPopover}
      />
      <Modal
        visible={open}
        transparent
        animationType={sheet ? 'fade' : 'none'}
        onRequestClose={() => setOpen(false)}
      >
        <Pressable
          style={sheet ? styles.modalBackdrop : styles.popoverBackdrop}
          onPress={() => setOpen(false)}
        >
          <Pressable
            onPress={() => {}}
            onLayout={(event) => setMenuHeight(event.nativeEvent.layout.height)}
            style={[
              cardStyle,
              {
                backgroundColor: colors.solid_color_primary,
                borderColor: colors.border_color_secondary,
              },
            ]}
          >
            <WorkThinkingPowerPanel
              level={level}
              onChange={commitLevel}
              colors={colors}
              iconName={showCompact ? 'brain' : 'bolt'}
            />
            <View style={[styles.autoApproveRow, { borderTopColor: colors.border_color_secondary }]}>
              <View style={styles.autoApproveCopy}>
                <Text size="s" weight="semibold">Auto-approve credits</Text>
                <Text size="xs" color="secondary">
                  Automatically approve spending from your available credits. Questions and other approvals still come to you.
                </Text>
              </View>
              <Switch
                value={autoApprove}
                onValueChange={changeAutoApprove}
                disabled={savingAutoApprove || taskStatus === 'queued'}
                accessibilityLabel="Auto-approve credits"
                accessibilityHint="Allow this chat to spend available credits and make task decisions without asking."
              />
            </View>
          </Pressable>
        </Pressable>
      </Modal>
    </View>
  );
}

WorkThinkingPowerButton.propTypes = {
  conversationId: PropTypes.string,
  compact: PropTypes.bool,
  isDisabled: PropTypes.bool,
};

const styles = StyleSheet.create({
  autoApproveRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 14,
    borderTopWidth: 1,
    paddingTop: 14,
    marginTop: 14,
  },
  autoApproveCopy: { flex: 1, gap: 4 },
  trigger: {
    flexDirection: 'row',
    alignItems: 'center',
    height: 34,
    paddingLeft: 10,
    paddingRight: 6,
    borderRadius: 18,
    gap: 6,
    ...(Platform.OS === 'web' ? { cursor: 'pointer', userSelect: 'none' } : {}),
  },
  triggerCompact: {
    paddingLeft: 8,
    paddingRight: 4,
    gap: 4,
  },
  triggerLabel: {
    maxWidth: 110,
  },
  panel: {
    gap: 10,
    width: '100%',
    minWidth: 0,
  },
  headerRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  headerName: {
    flexShrink: 1,
  },
  sliderHit: {
    height: 28,
    justifyContent: 'center',
    overflow: 'visible',
    position: 'relative',
    ...(Platform.OS === 'web' ? { cursor: 'pointer', userSelect: 'none', touchAction: 'none' } : {}),
  },
  stepRow: {
    ...StyleSheet.absoluteFillObject,
    flexDirection: 'row',
  },
  stepHit: {
    flex: 1,
    height: '100%',
  },
  track: {
    height: TRACK_HEIGHT,
    borderRadius: TRACK_HEIGHT / 2,
    overflow: 'visible',
    justifyContent: 'center',
  },
  trackFill: {
    position: 'absolute',
    left: 0,
    top: 0,
    bottom: 0,
    borderRadius: TRACK_HEIGHT / 2,
  },
  dot: {
    position: 'absolute',
    width: DOT_SIZE,
    height: DOT_SIZE,
    borderRadius: DOT_SIZE / 2,
    marginLeft: -DOT_SIZE / 2,
    top: (TRACK_HEIGHT - DOT_SIZE) / 2,
  },
  thumb: {
    position: 'absolute',
    width: THUMB_SIZE,
    height: THUMB_SIZE,
    borderRadius: THUMB_SIZE / 2,
    marginLeft: -THUMB_SIZE / 2,
    top: (TRACK_HEIGHT - THUMB_SIZE) / 2,
    borderWidth: StyleSheet.hairlineWidth,
    ...Platform.select({
      web: { boxShadow: '0 1px 4px rgba(0,0,0,0.35)' },
      default: {
        shadowColor: '#000',
        shadowOpacity: 0.25,
        shadowRadius: 3,
        shadowOffset: { width: 0, height: 1 },
        elevation: 2,
      },
    }),
  },
  metaRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    gap: 8,
  },
  metaRight: {
    textAlign: 'right',
    flexShrink: 1,
  },
  caption: {
    lineHeight: 16,
  },
  modalBackdrop: {
    flex: 1,
    backgroundColor: 'rgba(0,0,0,0.4)',
    justifyContent: 'flex-end',
    padding: 16,
  },
  modalCard: {
    borderRadius: 16,
    borderWidth: 1,
    padding: 14,
    marginBottom: 24,
  },
  popoverBackdrop: {
    flex: 1,
    backgroundColor: 'transparent',
  },
  popoverCard: {
    position: 'absolute',
    width: 300,
    borderRadius: 16,
    borderWidth: 1,
    padding: 12,
    ...Platform.select({
      web: { boxShadow: '0 10px 38px -10px rgba(0,0,0,0.45), 0 10px 20px -15px rgba(0,0,0,0.25)' },
      default: {
        shadowColor: '#000',
        shadowOpacity: 0.35,
        shadowRadius: 16,
        shadowOffset: { width: 0, height: 8 },
        elevation: 8,
      },
    }),
  },
});

export default React.memo(WorkThinkingPowerButton);
